<script setup>
  import { ref, getCurrentInstance, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { usePageLoadingStore } from '../../../../../../stores/pageLoading'
  import { useUserStore } from '../../../../stores/user'
  import JobFairJobNoticeDetailModal from '../../../../components/modal/jobFair/JobFairJobNoticeDetailModal.vue'
  import JobFairCorpInfoDetailModal from '../../../../components/modal/jobFair/JobFairCorpInfoDetailModal.vue'
  import JobFairResumeDetailModal from '../../../../components/modal/jobFair/JobFairResumeDetailModal.vue'
  import mypageJobFairService from '../../../../services/mypage/jobFair/mypageJobFairService'

  const route = useRoute()
  const router = useRouter()
  const loadingStore = usePageLoadingStore()
  const { user, isAuthenticated } = useUserStore()
  const globals = getCurrentInstance().appContext.config.globalProperties;
  const { $pageListBoardNo, $gbNameKR, $yyyymmddHHmmss, $yyyymmdd, alert, confirm } = globals;

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]);

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: 10 },
    { label:"30개씩 보기", value: 30 },
    { label:"50개씩 보기", value: 50 },
    { label:"100개씩 보기", value: 100 },
  ])

  const jobFairResumeModal = ref(null) // 이력서
  const jobNoticeModal = ref(null); // 채용공고
  const corpInfoModal = ref(null); // 기업정보

  const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  const retrieveList = () => {
    const paramObj = {
      pageNumber: currentPage.value || 0,
      size: pageSize.value || 10,
    };

    loadingStore.loading = true;
    mypageJobFairService.retrieveInterviewProposalEntryListPaging(paramObj)
      .then( (res) => {
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          // console.log(result);
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
        loadingStore.loading = false;
      })
      .catch( (error) =>  {
        loadingStore.loading = false;
      });
  }

  // 채용공고 보기
  const onClickJobNotice = async (jobNotice) => {
    await jobNoticeModal.value.show({
      jobNoticeSeq: jobNotice.jobNoticeSeq,
      corpId: jobNotice.corpId,
    });
  }

  // 기업정보 보기
  const onClickCorp = async (jobNotice) => {
    const result = await corpInfoModal.value.show({
      corpId: jobNotice.corpId,
      interestYn: jobNotice.interestYn,
    });
  }

  const onClickMyResume = async (resumeId, delYn) => {
    if (delYn === 'Y') {
      alert(["삭제된 이력서 입니다."]);
    } else {
     await jobFairResumeModal.value.show({
       resumeId,
       hideFooterBtn: true,
     });
    }
  }

  // 받은 면접제안 상태변경
  const onClickIntvPropState = async (row, stateGb) => {
    let modalContent, okButtonText;
    if (stateGb === '02') {
      modalContent = ["면접 제안을 수락하시겠습니까?", "'제안수락'으로 상태가 변경됩니다."];
      okButtonText = "제안 수락";
    } else {
      modalContent = ["면접 제안을 거절하시겠습니까?", "'제안거절'로 상태가 변경됩니다."];
      okButtonText = "제안 거절";
    }
    const ok = await confirm(modalContent, okButtonText);
    if(ok) {
      const paramObj = {
        jobNoticeSeq: row.jobNoticeSeq, 
        resumeId: row.resumeId,
        intvPropStateGb: stateGb
      };

      mypageJobFairService.updateIntvPropStateGb(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            //const result = res.data.result;
            alert('변경하였습니다.');
            retrieveList();
          } else {
            alert('변경을 실패하였습니다.');
          }
        })
        .catch( (res) =>  {
          loadingStore.loading = false;
          alert('변경을 실패하였습니다.');
        });
    }
  }

  onMounted(() => {
    retrieveList();
  })
</script>

<template>
 <div class="table-form">
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
    </div>
    <div class="table-form">
      <table class="table-form board-mobile-vertical">
        <tr>
          <th>번호</th>
          <th>기업명</th>
          <th>채용공고 제목</th>
          <th>관심 카테고리</th>
          <th>면접 방식</th>
          <th>내 이력서 제목</th>
          <th>제안 받은일</th>
          <th>상태</th>
          <th>상태변경</th>
        </tr>
        <tr v-for="item in list">
          <td>{{ item.boardNo }}</td>
          <td class="clickable blue text-overflow" @click="onClickCorp(item)">{{ item.corpName }}</td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(item)">{{ item.jobName }}</td>
          <td>{{ item.interests }}</td>
          <td>
            {{ $gbNameKR('INTV_GB',item.intvGb) }}
          </td>
          <td>
            <p class="clickable blue text-overflow long-overflow" @click="onClickMyResume(item.resumeId, item.delYn)">
              {{ item.title }}
            </p>
          </td>
          <td>{{ $yyyymmddHHmmss(item.createDate) }}</td>
          <td>
            <span v-if="item.intvPropStateGb === '01'">{{ $gbNameKR('USER_INTV_PROP_STATE_GB',item.intvPropStateGb) }}</span>
            <span v-else-if="item.intvPropStateGb === '02' || item.intvPropStateGb === '05'" class="blue">{{ $gbNameKR('USER_INTV_PROP_STATE_GB',item.intvPropStateGb) }}</span>
            <span v-else-if="item.intvPropStateGb === '03' || item.intvPropStateGb === '06'" class="red">{{ $gbNameKR('USER_INTV_PROP_STATE_GB',item.intvPropStateGb) }}</span>
            <span v-else class="gray">{{ $gbNameKR('USER_INTV_PROP_STATE_GB',item.intvPropStateGb) }}</span>
          </td>
          <td>
            <div v-if="item.intvPropStateGb === '01'">
              <KSATextButton text="제안수락" @click="onClickIntvPropState(item, '02')" class="clickable black"></KSATextButton>
              <KSATextButton text="제안거절" @click="onClickIntvPropState(item, '03')" class="clickable black"></KSATextButton>
            </div>
            <div v-else>-</div>
          </td>
        </tr>
      </table>
    </div>
    <div class="result-none lecture-none" v-if="list.length === 0">
      받은 면접제안 내역이 없습니다.
    </div>
    <Paginate
        v-if="list.length > 0"
        v-model="currentPage"
        :page-count="totalPages"
        :click-handler="onSelectPage"
    />
  </div>
  <JobFairResumeDetailModal ref="jobFairResumeModal" /> <!-- 이력서 보기 -->
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  <JobFairCorpInfoDetailModal ref="corpInfoModal" /> <!-- 기업정보 보기 -->
</template>

<style scoped>
.text-overflow.long-overflow {
  max-width: 180px;
}
</style>
